import { chunk, compact, uniq } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Appointment } from 'app/generated/graphql'
import { type FilterObject, filtersByAttribute } from 'app/filter.utils'
import { useDrawerContact } from 'app/hooks/useDrawerContact'
import { useInitialTableSort } from 'app/hooks/useInitialTableSort'
import { useSavedListTab } from 'app/hooks/useSavedListTab'
import { useStickyLocationDefault } from 'app/hooks/useStickyLocation'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { client } from 'app/lib/urqlClient'
import { AppointmentStore } from 'app/stores/AppointmentStore'
import { appointmentColumns, appointmentFilterableColumns } from 'app/table-columns/appointments'
import type { AppointmentRow, PatientLink } from 'app/types/appointment-row'
import type { ContactJson } from 'app/types/entities'
import type { LocationConsumer, ProviderRecord } from 'app/types/provider-directory'
import { useHandleSearchParams } from 'app/utils'
import {
  APPOINTMENTS_WHEN_DEFAULT,
  type AppointmentsWhen,
  EMPTY_PROVIDER_DIRECTORY,
  PATIENT_ID_LIMIT,
  appointmentPageFilters,
  appointmentStartLabel,
  appointmentStatusLabel,
  appointmentsForPatients,
  appointmentsSearch,
  contactsForPatients,
  isAppointmentsWhen,
  patientLinks,
  providerDirectory,
  whenSearch,
} from 'lib/appointmentsPage'
import { bg } from 'lib/background'
import { contactNameTerms } from 'lib/contactSearch'
import { filtersToSearch } from 'lib/listSearch'
import { log, logError } from 'lib/logging'

const PAGE_SIZE = 50
const PROVIDER_LIMIT = 500
const LOCATION_LIMIT = 500
const NAME_MATCH_LIMIT = 200
// One patient is sometimes carried by more than one contact (a merge left behind); read room for a few.
const CONTACTS_PER_PATIENT = 3
const EXPORT_PATIENT_BATCH = 100
const PATIENT_FIELDS = `id name patientIds`
const PROVIDER_FIELDS = `vendorId displayName resourceName kollaConsumerId`
const LOCATION_FIELDS = `id kollaConsumerId`

type PatientContact = Pick<ContactJson, `id` | `name` | `patientIds`>

async function readPatientLinks(patientIds: string[]): Promise<Map<string, PatientLink | null>> {
  const { hits } = await gqlFetchList<PatientContact>(
    `contacts`,
    { search: contactsForPatients(patientIds), pagination: { limit: patientIds.length * CONTACTS_PER_PATIENT } },
    PATIENT_FIELDS,
  )
  return patientLinks(hits, patientIds)
}

export function useAppointmentsPage() {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  // The page owns its list: the contact drawer lists one patient's appointments through the
  // app-wide store, which would otherwise replace this table and its count.
  const appointmentStore = useMemo(() => new AppointmentStore(client), [])
  const saved = useSavedListTab(`appointments`)
  const whenParam = searchParams.get(`when`)
  // A saved list's rule defines who is on it, so it opens on every date with no booked-only
  // chip; a When the user picks still wins. A pending list counts, so the scope never flips.
  const onSavedList = !saved.settled || !!saved.list
  const when: AppointmentsWhen = isAppointmentsWhen(whenParam)
    ? whenParam
    : onSavedList
      ? `all`
      : APPOINTMENTS_WHEN_DEFAULT
  // One clock per visit: a page left open past midnight keeps its day until it is reloaded.
  const today = useMemo(() => new Date(), [])
  const query = (searchParams.get(`query`) ?? ``).trim()
  const filtersParam = searchParams.get(`filters`)
  const join = searchParams.get(`join`)
  const sortParam = searchParams.get(`sort`)
  const stickyLocation = useStickyLocationDefault()
  const stickyLocationKey = stickyLocation.join(`,`)
  const pageFilters = useMemo(
    () => appointmentPageFilters(when, today, stickyLocation),
    [when, today, stickyLocationKey],
  )

  const [providers, setProviders] = useState(EMPTY_PROVIDER_DIRECTORY)
  useEffect(() => {
    bg(
      Promise.all([
        gqlFetchList<ProviderRecord>(`providers`, { pagination: { limit: PROVIDER_LIMIT } }, PROVIDER_FIELDS),
        gqlFetchList<LocationConsumer>(`locations`, { pagination: { limit: LOCATION_LIMIT } }, LOCATION_FIELDS),
      ])
        .then(([providerList, locationList]) => {
          if (providerList.hits.length >= PROVIDER_LIMIT) {
            log(`useAppointmentsPage: provider list hit the ${PROVIDER_LIMIT} cap; some names may be missing`)
          }
          setProviders(providerDirectory(providerList.hits, locationList.hits))
        })
        .catch((err) => logError(`Error reading providers`, err)),
    )
  }, [])

  // The answer is kept with the query it was asked for, so a new query never searches on the last one's patients.
  const [nameMatch, setNameMatch] = useState<{ query: string; term: string }>()
  const nameSearch = contactNameTerms(query).join(` `)
  useEffect(() => {
    if (!nameSearch) return
    let current = true
    bg(
      gqlFetchList<Pick<ContactJson, `patientIds`>>(
        `contacts`,
        { search: nameSearch, pagination: { limit: NAME_MATCH_LIMIT } },
        `patientIds`,
      )
        .then(({ hits }) => {
          const patientIds = uniq(hits.flatMap((hit) => hit.patientIds ?? []))
          if (hits.length >= NAME_MATCH_LIMIT || patientIds.length > PATIENT_ID_LIMIT) {
            log(
              `useAppointmentsPage: name search hit a cap (${hits.length} contacts, ${patientIds.length} patients); some matches are not searched`,
            )
          }
          if (current) setNameMatch({ query, term: appointmentsForPatients(patientIds) })
        })
        .catch((err) => {
          logError(`Error matching a patient name`, err)
          if (current) setNameMatch({ query, term: appointmentsForPatients([]) })
        }),
    )
    return () => {
      current = false
    }
  }, [query])
  const nameAnswer = nameMatch?.query === query ? nameMatch.term : undefined
  // A query with no name in it (a bare `@`) matches no one rather than everyone.
  const queryTerm = nameSearch ? nameAnswer : appointmentsForPatients([])
  const patientTerm = query ? queryTerm : undefined

  const listPage = saved.list?.page
  const listRule = useMemo(
    () =>
      listPage
        ? filtersToSearch((listPage.filters ?? []) as unknown as FilterObject[], listPage.join === `or` ? `or` : `and`)
        : undefined,
    [listPage],
  )
  // Until a `?tab=` list's rule is in hand the page would read every appointment, which it must not show.
  const searchReady = (!query || patientTerm !== undefined) && saved.settled

  const search = useMemo(
    () => appointmentsSearch({ when, today, filtersParam, join, pageFilters, patientTerm, listRule }),
    [when, today, filtersParam, join, pageFilters, patientTerm, listRule],
  )
  const scopeSort = whenSearch(when, today).sort
  const [sortField, sortDirection] = sortParam?.split(`:`) ?? []
  const direction: `ASC` | `DESC` = sortDirection?.toUpperCase() === `ASC` ? `ASC` : `DESC`
  const sort = sortField ? { field: sortField, direction } : scopeSort
  useEffect(() => {
    if (!searchReady) return
    bg(appointmentStore.fetchList({ search, pagination: { page: 0, limit: PAGE_SIZE }, sort }))
  }, [search, searchReady, sort.field, sort.direction])
  // The header arrow names the column; the server sorts on `date,time` either way.
  const initialSort = useInitialTableSort(`date:${scopeSort.direction.toLowerCase()}`)

  const [patients, setPatients] = useState<ReadonlyMap<string, PatientLink | null>>(new Map())
  const unresolvedKey = uniq(compact(appointmentStore.hits.map((hit) => hit.patientId)))
    .filter((patientId) => !patients.has(patientId))
    .join(`,`)
  useEffect(() => {
    if (!unresolvedKey) return
    bg(
      readPatientLinks(unresolvedKey.split(`,`))
        .then((links) => setPatients((prev) => new Map([...prev, ...links])))
        .catch((err) => logError(`Error reading appointment patients`, err)),
    )
  }, [unresolvedKey])

  const tableData = appointmentStore.tableData
  // Until a `?tab=` list is in hand the store still holds the last unfiltered read, which the
  // table and the export must not offer as the list's rows.
  const data = useMemo(
    () =>
      saved.settled && tableData
        ? {
            total: tableData.total,
            hits: tableData.hits.map(
              (hit): AppointmentRow => ({ ...hit, patient: hit.patientId ? patients.get(hit.patientId) : null }),
            ),
          }
        : undefined,
    [saved.settled, tableData, patients],
  )

  // Past and All can span years, so their Date column spells one out on every row; elsewhere only a day outside this year does.
  const withYear = when === `past` || when === `all`
  // handleSearchParams reads the live URL when it runs, so the first render's copy serves every click.
  const columns = useMemo(
    () =>
      appointmentColumns({
        onOpenPatient: (contactId) => handleSearchParams({ drawer: contactId }),
        providers,
        withYear,
        today,
      }),
    [providers, withYear, today],
  )
  const filterableColumns = useMemo(
    () => appointmentFilterableColumns({ preAppliedFilters: filtersByAttribute(pageFilters), providers }),
    [pageFilters, providers],
  )

  const enrichExportRows = async (rows: Appointment[]) => {
    const batches = chunk(uniq(compact(rows.map((row) => row.patientId))), EXPORT_PATIENT_BATCH)
    const links = new Map((await Promise.all(batches.map(readPatientLinks))).flatMap((batch) => [...batch]))
    return rows.map((row) => ({
      ...row,
      patientName: (row.patientId && links.get(row.patientId)?.name) || ``,
      // The CSV always carries the year, whatever the on-screen scope, so a spreadsheet never loses it.
      date: appointmentStartLabel(row, { withYear: true }),
      status: appointmentStatusLabel(row.status),
      providerId: providers.nameAt(row.locationId, row.providerId),
    }))
  }

  const drawer = useDrawerContact()

  return {
    when,
    setWhen: (next: AppointmentsWhen) => handleSearchParams({ when: next, sort: undefined, page: undefined }),
    data,
    columns,
    filterableColumns,
    initialSort,
    search,
    searchReady,
    loadMore: appointmentStore.fetchNextPage,
    enrichExportRows,
    list: saved.list && { name: saved.list.name, edit: saved.list.edit },
    drawer,
  }
}
